import { useState } from "react";
import type { Customer360Snapshot } from "../../types/crm";
import {
  useAddAlias,
  useRemoveAlias,
  usePromoteAlias,
} from "../../helpers/hooks/use-alias-mutations";

interface Props {
  customerId: string;
  aliases: Customer360Snapshot["customer"]["aliases"];
}

function isValidEmail(s: string): boolean {
  return /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(s);
}

/**
 * Inline editor for a customer's email aliases. Rendered in OverviewSection.
 * Primary alias gets a pill badge and can't be removed (promote another
 * alias first). Non-primary aliases get "Make primary" + "Remove" actions.
 */
export function AliasEditor({ customerId, aliases }: Props) {
  const [newEmail, setNewEmail] = useState("");
  const [error, setError] = useState<string | null>(null);

  const addMut = useAddAlias(customerId);
  const removeMut = useRemoveAlias(customerId);
  const promoteMut = usePromoteAlias(customerId);

  const handleAdd = () => {
    setError(null);
    const trimmed = newEmail.trim();
    if (!trimmed) return;
    if (!isValidEmail(trimmed)) {
      setError("Not a valid email address");
      return;
    }
    if (aliases.some((a) => a.email.toLowerCase() === trimmed.toLowerCase())) {
      setError("That email is already on file");
      return;
    }
    addMut.mutate(trimmed, {
      onSuccess: () => setNewEmail(""),
      onError: (err: any) => {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to add alias",
        );
      },
    });
  };

  const busy = addMut.isPending || removeMut.isPending || promoteMut.isPending;

  return (
    <div>
      <ul className="space-y-1.5">
        {aliases.map((alias) => (
          <li
            key={alias.id}
            className="flex items-center gap-2 text-sm group"
          >
            <span className="font-medium text-content-primary break-all">
              {alias.email}
            </span>
            {alias.isPrimary && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700">
                primary
              </span>
            )}
            {alias.source && alias.source !== "manual" && (
              <span className="text-[10px] text-content-tertiary">
                · {alias.source}
              </span>
            )}
            {!alias.isPrimary && (
              <div className="ml-auto flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  type="button"
                  onClick={() => promoteMut.mutate(alias.id)}
                  disabled={busy}
                  className="text-xs text-primary hover:underline disabled:opacity-50"
                  title="Make this the primary email"
                >
                  Make primary
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (
                      window.confirm(`Remove ${alias.email} from this customer?`)
                    ) {
                      removeMut.mutate(alias.id);
                    }
                  }}
                  disabled={busy}
                  className="text-xs text-red-600 hover:underline disabled:opacity-50"
                >
                  Remove
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>

      <div className="mt-3 flex items-center gap-2">
        <input
          type="email"
          value={newEmail}
          onChange={(e) => {
            setNewEmail(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleAdd();
          }}
          placeholder="add another email…"
          disabled={busy}
          className="flex-1 px-2.5 py-1.5 text-sm border border-border-primary rounded-md focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={busy || !newEmail.trim()}
          className="px-3 py-1.5 text-xs font-medium text-content-secondary bg-surface-secondary border border-border-primary rounded-md hover:bg-surface-hover disabled:opacity-50"
        >
          {addMut.isPending ? "Adding…" : "Add"}
        </button>
      </div>

      {error && (
        <div className="mt-1.5 text-xs text-red-600">{error}</div>
      )}
    </div>
  );
}
